"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Layers, ShieldCheck, ArrowUpRight, Ruler, Sparkles, Compass } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FeatureItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const includedFeatures: FeatureItem[] = [{
  id: "materials",
  title: "Decking Material Options",
  description: "Premium capped composites, PVC, sustainable tropical hardwoods, and pressure-treated southern pine.",
  icon: Sparkles
}, {
  id: "structural",
  title: "Structural Rebuilds",
  description: "Deep concrete footings, reinforced ledger board attachments, and joist framing built to Brookhaven building codes.",
  icon: Ruler
}, {
  id: "railings",
  title: "Railings",
  description: "Powder-coated aluminum balusters, modern horizontal cable systems, tempered glass, or classic milled wood.",
  icon: ShieldCheck
}, {
  id: "multilevel",
  title: "Multi-Level Decks",
  description: "Tiered layouts with integrated under-deck dry drainage systems for shaded lower-patio living.",
  icon: Layers
}, {
  id: "stairs",
  title: "Stairs",
  description: "Wide-tread staircases, intermediate resting landings, and low-voltage recessed step lighting.",
  icon: Compass
}, {
  id: "weatherproofing",
  title: "Weatherproofing",
  description: "Joist tape, copper flashing, hidden fastener systems, and commercial-grade protective sealants.",
  icon: CheckCircle2
}];
export default function DeckBuildingHeroAnswer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section ref={ref} id="deckbuildingheroanswer" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Main Answer Column */}
          <div className={`lg:col-span-7 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 font-mono tracking-wide uppercase text-xs">
                Brookhaven Outdoor Living
              </Badge>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15] mb-6">
              Custom Deck Building &amp; Structural Remodeling in Brookhaven
            </h1>

            {/* Direct 2-3 sentence answer */}
            <div className="p-6 rounded-lg bg-card border border-border shadow-xs mb-8">
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-default">
                Deck building and remodeling in Brookhaven involves engineering safe, code-compliant outdoor foundations tailored to Georgia’s sloping tree-lined lots and humid climate. We replace failing framing, expand usable entertaining footprints, and install enduring weather-resistant decking boards and code-certified rail assemblies. Every installation is designed for seamless indoor-outdoor architectural continuity and decade-long durability.
              </p>
            </div>

            {/* What's Included Section */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-serif font-medium text-foreground tracking-tight">
                  What&apos;s Included
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Complete turnkey deck construction and modernization specifications.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {includedFeatures.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.id} data-index={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border border-border/60 hover:bg-muted/70 transition-colors">
                      <div className="shrink-0 p-2 rounded-md bg-primary/15 text-primary-foreground">
                        <Icon className="size-4 text-secondary" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground leading-snug">
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="font-medium shadow-sm">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an Estimate
                    <ArrowUpRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/OurProjectsPage">
                    View Completed Decks
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Photographic Image Column */}
          <div className={`lg:col-span-5 transition-all duration-700 delay-150 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-xl border border-border shadow-md aspect-4/5 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_composite_deck_remodel.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_composite_deck_remodel.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_composite_deck_remodel.png-wvc-sizes" alt="Modern elevated multi-level composite deck with black aluminum railings overlooking a Brookhaven wooded backyard" loading="lazy" decoding="async" className="w-full h-full object-cover object-center hover:scale-102 transition-transform duration-500 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-background/90 backdrop-blur-xs border border-border/80 shadow-xs">
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Brookhaven, GA Project
                  </p>
                  <p className="text-sm font-semibold text-foreground mt-0.5">
                    Multi-level composite deck with integrated cable rail system
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="relative overflow-hidden rounded-lg border border-border shadow-xs aspect-4/3 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_stair_landing_framing_detail.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_stair_landing_framing_detail.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_stair_landing_framing_detail.png-wvc-sizes" alt="Precision engineered deck stairs and structural footing detail during renovation" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                </div>
                <div className="relative overflow-hidden rounded-lg border border-border shadow-xs aspect-4/3 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/covered_under_deck_weatherproofing_drainage.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/covered_under_deck_weatherproofing_drainage.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/covered_under_deck_weatherproofing_drainage.png-wvc-sizes" alt="Under deck ceiling drainage and weatherproofing assembly" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}